import { NotificationType, useTranslation } from "@next-platform/client-common-core";
import { useEventCapabilityIsUsed } from "@next-platform/client-common-core/services/databricks";
import type { MediaFile, MediaFileWithPublicFields } from "@next-platform/packages-ts-types";
import { SubCapabilityKey } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useRef, useState } from "react";

import useToast from "@/hooks/useToast";
import downloadMediaFile, { getMediaFileName } from "@/utils/downloadMediaFile";

type LiveMediaFile = MediaFile | MediaFileWithPublicFields;

type MediaFileDownload = {
  readonly onDownload: (() => void) | undefined;
  readonly isDownloading: boolean;
  readonly fallbackUrl: string | undefined;
  /** The "open original" link is the same Feature as the download, so a press on it counts as one too. */
  readonly onOpenFallback: () => void;
};

/**
 * Stepping keeps the details pane mounted, so the busy state names its file and the outcome only reaches the
 * user while that file is still the one on screen.
 */
export default function useMediaFileDownload({ id, file }: { id: string; file?: LiveMediaFile }): MediaFileDownload {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const raiseEventCapabilityIsUsed = useEventCapabilityIsUsed();
  const [stateDownloadingFileId, setStateDownloadingFileId] = useState<string | null>(null);
  const [stateFallbackFileId, setStateFallbackFileId] = useState<string | null>(null);
  const refShownFileId = useRef(id);

  useEffect(() => {
    refShownFileId.current = id;
  }, [id]);

  const releaseDownload = useCallback((fileId: string) => setStateDownloadingFileId((current) => (current === fileId ? null : current)), []);

  const handleDownload = useCallback(() => {
    if (!file?.url) {
      return;
    }
    const { id: fileId, url, content_type: contentType } = file;
    if (stateDownloadingFileId === fileId) {
      return;
    }
    raiseEventCapabilityIsUsed(SubCapabilityKey.MediaDownload);
    setStateDownloadingFileId(fileId);
    setStateFallbackFileId(null);
    void downloadMediaFile({ url, fileName: getMediaFileName(fileId, contentType) })
      .then((outcome) => {
        if (outcome === "saved" || refShownFileId.current !== fileId) {
          return;
        }
        // No "open original" link for a file the CDN no longer serves: it would open the raw error page.
        if (outcome === "unavailable") {
          displayToast({ message: t("mediaFile:unavailable.notFound.title"), type: NotificationType.error });
          return;
        }
        displayToast({ message: t("gallery:mediaFilePreview.downloadFailed"), type: NotificationType.error });
        setStateFallbackFileId(fileId);
      })
      .finally(() => releaseDownload(fileId));
  }, [displayToast, file, raiseEventCapabilityIsUsed, releaseDownload, stateDownloadingFileId, t]);

  const handleOpenFallback = useCallback(() => raiseEventCapabilityIsUsed(SubCapabilityKey.MediaDownload), [raiseEventCapabilityIsUsed]);

  return {
    onDownload: file?.url ? handleDownload : undefined,
    isDownloading: stateDownloadingFileId === file?.id,
    fallbackUrl: file?.url && stateFallbackFileId === file.id ? file.url : undefined,
    onOpenFallback: handleOpenFallback,
  };
}
